import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Upload, Trash2 } from "lucide-react";

export default function AdminCOAs() {
  const { getToken } = useAuth();
  const [coas, setCoas] = useState([]);
  const [meta, setMeta] = useState({ product_name: "", batch_number: "", size: "", testing_lab: "", report_date: "" });
  const fileRef = useRef();

  const load = async () => {
    const r = await api.get("/coas");
    setCoas(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Select a PDF");
    const fd = new FormData();
    Object.entries(meta).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    try {
      const token = await getToken();
      await api.post("/admin/coas", fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("COA uploaded");
      setMeta({ product_name: "", batch_number: "", size: "", testing_lab: "", report_date: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — check R2 keys");
    }
  };

  const archive = async (id) => {
    const token = await getToken();
    await api.delete(`/admin/coas/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success("Archived");
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <p className="text-xs uppercase tracking-[0.3em] text-[#5E6B51]">Documentation</p>
      <h1 className="font-serif text-4xl mt-2 mb-10">Certificates of Analysis</h1>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#A8B59A]/20 p-6 mb-10 grid md:grid-cols-6 gap-3">
        <Input data-testid="coa-product" required placeholder="Product name" value={meta.product_name} onChange={(e) => setMeta({ ...meta, product_name: e.target.value })} className="md:col-span-2 rounded-xl" />
        <Input data-testid="coa-batchno" required placeholder="Batch #" value={meta.batch_number} onChange={(e) => setMeta({ ...meta, batch_number: e.target.value })} className="rounded-xl" />
        <Input data-testid="coa-size-in" placeholder="Size" value={meta.size} onChange={(e) => setMeta({ ...meta, size: e.target.value })} className="rounded-xl" />
        <Input data-testid="coa-lab" placeholder="Lab" value={meta.testing_lab} onChange={(e) => setMeta({ ...meta, testing_lab: e.target.value })} className="rounded-xl" />
        <Input data-testid="coa-date" placeholder="Report date" value={meta.report_date} onChange={(e) => setMeta({ ...meta, report_date: e.target.value })} className="rounded-xl" />
        <input ref={fileRef} type="file" accept="application/pdf" className="md:col-span-5 text-sm" data-testid="coa-file" />
        <Button data-testid="coa-upload" type="submit" className="rounded-xl bg-[#59394F] hover:bg-[#432A3B] text-white"><Upload size={14} className="mr-1" />Upload</Button>
      </form>

      <div className="bg-white rounded-2xl border border-[#A8B59A]/20 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-[#5E4D5A] border-b border-[#A8B59A]/20">
              <th className="p-4">Product</th><th className="p-4">Batch</th><th className="p-4">Size</th><th className="p-4">Lab</th><th className="p-4">Date</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {coas.map((c) => (
              <tr key={c.id} className="border-b border-[#A8B59A]/10">
                <td className="p-4 font-medium">{c.product_name}</td>
                <td className="p-4">{c.batch_number}</td>
                <td className="p-4 text-[#5E4D5A]">{c.size || "—"}</td>
                <td className="p-4 text-[#5E4D5A]">{c.testing_lab || "—"}</td>
                <td className="p-4 text-[#5E4D5A]">{c.report_date || "—"}</td>
                <td className="p-4"><button onClick={() => archive(c.id)} className="text-[#59394F]"><Trash2 size={14} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
